import { useEffect, useState } from "react";

import { Pagination } from "@mui/material";
import {
  ArchiveIcon,
  HomeIcon,
  PersonIcon,
  SewingPinFilledIcon,
} from "@radix-ui/react-icons";
import {
  CheckCircle2,
  Clock3,
  Edit2,
  Eye,
  FileText,
  Filter,
  Search,
  Upload,
  X,
  XCircle,
} from "lucide-react";
import toast from "react-hot-toast";
import { DetailIntention } from "../../components/Abonnement/DetailIntention";
import { formatFileSize } from "../../components/Ajouter-intention/DemandeIntention";
import { Pbo } from "../../components/Widget/Pbo";
import { Periode } from "../../components/Widget/Periode";
import { find, store } from "../../services/service";
import {
  BASE_URL,
  DEGELER_POSTPAID,
  LISTE_POSTPAID,
  MODIFIER_POSTPAID,
  MODIFIER_STATUS_POSTPAID,
} from "../../Utils/constant";
import { checkTagValidity } from "../../Utils/Utils";
import { UpdateDemandeIntention } from "./UpdateDemandeIntention";

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────

const StatutBadge = ({ statut }) => {
  const config = {
    VALIDE: {
      label: "Validé",
      icon: <CheckCircle2 className="w-3 h-3" />,
      cls: "bg-emerald-50 text-emerald-700 border border-emerald-200",
    },
    REJETE: {
      label: "Rejeté",
      icon: <XCircle className="w-3 h-3" />,
      cls: "bg-red-50 text-red-700 border border-red-200",
    },
    EN_ATTENTE: {
      label: "En attente",
      icon: <Clock3 className="w-3 h-3" />,
      cls: "bg-amber-50 text-amber-700 border border-amber-200",
    },
  };
  const c = config[statut] ?? config["EN_ATTENTE"];
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${c.cls}`}
    >
      {c.icon}
      {c.label}
    </span>
  );
};

// ─────────────────────────────────────────────
// Modal Statut
// ─────────────────────────────────────────────
const StatutModal = ({ item, onClose, onStatutUpdated }) => {
  const [selectedStatut, setSelectedStatut] = useState(
    item?.statut || "EN_ATTENTE",
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [motif, setMotif] = useState("");
  const [message, setMessage] = useState("");
  const statutOptions = [
    {
      value: "VALIDE",
      label: "Valider le dossier",
      icon: <CheckCircle2 className="w-4 h-4" />,
      accent: "border-emerald-400 bg-emerald-50 text-emerald-800",
    },
    {
      value: "REJETE",
      label: "Rejeter le dossier",
      icon: <XCircle className="w-4 h-4" />,
      accent: "border-red-400 bg-red-50 text-red-800",
    },
  ];

  const handleChangeStatut = async () => {
    setMessage("");
    if (selectedStatut === item?.statusValidationPostpaid) {
      setMessage("Veuillez sélectionner un statut différent");
      return;
    }
    if (motif.trim() === "" && selectedStatut === "REJETE") {
      setMessage("Veuillez fournir un motif de rejet");
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await store(
        `${MODIFIER_STATUS_POSTPAID}/${item.id}`,
        "application/json",
        { statut: selectedStatut, motif: motif },
      );
      if (res.status === 200) {
        toast.success("Statut modifié avec succès");
        onStatutUpdated?.();
        onClose();
      } else {
        setMessage(res?.donnee || "Erreur lors de la modificationee");
      }
    } catch (error) {
      setMessage("Erreur lors de la modification");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <dialog
      open
      className="modal bg-black/60 backdrop-blur-sm"
      style={{ zIndex: 9999 }}
    >
      <div className="modal-box max-w-sm bg-white rounded-2xl p-0 overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="font-bold text-gray-900 text-base">
              Changer le statut
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">
              Dossier N° {item?.numeroTicket}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-red-50 hover:text-red-500 flex items-center justify-center transition-colors text-gray-500"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {message && (
          <div className="mb-4 px-4 py-2 rounded bg-red-50 text-red-700 text-sm">
            {message}
          </div>
        )}
        {/* Statut actuel */}
        <div className="px-6 pt-5 pb-2">
          <p className="text-xs text-gray-400 uppercase tracking-wide font-semibold mb-2">
            Statut actuel
          </p>
          <StatutBadge statut={item?.statusValidationPostpaid} />
        </div>

        {/* Sélection */}
        <div className="px-6 pb-5 pt-3 space-y-4">
          <label className="text-xs text-gray-400 uppercase tracking-wide font-semibold mb-2 block">
            Nouveau statut
          </label>
          <select
            value={selectedStatut}
            onChange={(e) => setSelectedStatut(e.target.value)}
            className="select select-bordered w-full text-sm"
          >
            <option value="">Selectionnez un statut</option>
            {statutOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>

          {selectedStatut === "REJETE" && (
            <div>
              <label className="block text-xs text-gray-400 uppercase tracking-wide font-semibold mb-2">
                Motif du rejet
              </label>
              <input
                type="text"
                value={motif}
                placeholder="Motif du rejet"
                className="w-full mt-1 px-4 py-2 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-red-300 text-sm"
                onChange={(e) => setMotif(e.target.value)}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-100 transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={handleChangeStatut}
            disabled={
              isSubmitting || selectedStatut === item?.statusValidationPostpaid
            }
            className="flex-1 px-4 py-2.5 rounded-xl bg-black text-white text-sm font-semibold disabled:bg-gray-300 disabled:cursor-not-allowed hover:bg-gray-800 transition-colors flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Envoi...
              </>
            ) : (
              "Confirmer"
            )}
          </button>
        </div>
      </div>
      <div className="modal-backdrop" onClick={onClose} />
    </dialog>
  );
};

// ─────────────────────────────────────────────
// Modal Documents
// ─────────────────────────────────────────────
const DocumentsModal = ({ item, onClose, onDocumentUpdated }) => {
  const documents = item?.listeDocuements || [];
  const [editingDocId, setEditingDocId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [postPaidDocuments, setPostPaidDocuments] = useState([]);

  const documentTypes = [
    { label: "CNI du représentant (recto)", value: "CNI_RECTO_REPRESENTANT" },
    { label: "CNI du représentant (verso)", value: "CNI_VERSO_REPRESENTANT" },
    { label: "Statuts de l'entreprise", value: "STATUTS" },
    { label: "Contrat de bail", value: "CONTRAT_BAIL" },
    { label: "Contrat FTTH", value: "CONTRAT_FTTH" },
    { label: "Bon de commande", value: "BON_DE_COMMANDE" },
  ];

  const handleAddDocument = (e, documentType) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const newDoc = {
      id: Date.now() + Math.random(),
      type: documentType,
      file,
      name: file.name,
      size: file.size,
    };

    console.log(newDoc);

    setPostPaidDocuments((prev) => [
      ...prev.filter((doc) => doc.type !== documentType),
      newDoc,
    ]);
    e.target.value = null;
  };

  const handleRemoveDocument = (documentId) => {
    setPostPaidDocuments((prev) => prev.filter((d) => d.id !== documentId));
  };

  const handleModifyType = async () => {
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      postPaidDocuments.forEach((doc, index) => {
        formData.append(`documents[${index}].fichier`, doc.file);
        formData.append(`documents[${index}].typeFichier`, doc.type);
      });
      const res = await store(
        `${MODIFIER_POSTPAID}/${item.id}`,
        "multipart/form-data",
        formData,
      );
      if (res.status === 200) {
        setEditingDocId(null);
        setPostPaidDocuments([]);
        onClose();
        toast.success("Document modifié avec succès");
        onDocumentUpdated?.();
      } else {
        toast.error("Erreur lors de la modification");
      }
    } catch {
      toast.error("Erreur lors de la modification");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <dialog
      id={`file${item?.id}`}
      open
      className="modal bg-black/60 backdrop-blur-sm"
      style={{ zIndex: 9999 }}
    >
      <div className="modal-box w-11/12 max-w-3xl bg-white rounded-2xl p-0 overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div>
              <h2 className="font-bold text-gray-900 text-lg font-mtn">
                Dossier — N° {item?.numeroTicket}
              </h2>
              <p className="text-xs text-gray-400 mt-0.5">
                {documents.length} / {documentTypes.length} types complétés
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-red-50 hover:text-red-500 flex items-center justify-center transition-colors text-gray-500"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Corps */}
        <div className="overflow-y-auto max-h-[65vh] p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {documentTypes.map((docType) => {
              const docForType = documents.find(
                (d) => d.typeFichier === docType.value,
              );
              const isEditing = editingDocId === docForType?.id;
              const uploadedForType = postPaidDocuments.find(
                (d) => d.type === docType.value,
              );

              return (
                <div
                  key={docType.value}
                  className={`rounded-2xl border-2 overflow-hidden transition-all duration-200 ${
                    isEditing
                      ? "border-amber-400 shadow-md"
                      : docForType
                        ? "border-amber-300 shadow-sm"
                        : "border-gray-200"
                  }`}
                >
                  {/* Titre */}
                  <div
                    className={`px-4 py-3 flex items-center justify-between border-b ${
                      isEditing
                        ? "border-amber-100 bg-amber-50"
                        : docForType
                          ? "border-amber-100 bg-amber-50"
                          : "border-gray-100 bg-gray-50"
                    }`}
                  >
                    <span className="text-xs font-semibold text-gray-700 truncate">
                      {docType.label}
                    </span>
                    {docForType && !isEditing ? (
                      <span className="flex-shrink-0 text-xs bg-amber-100 text-amber-700 font-semibold px-2 py-0.5 rounded-full">
                        ✓
                      </span>
                    ) : !docForType && !isEditing ? (
                      <span className="flex-shrink-0 text-xs bg-gray-100 text-gray-400 px-2 py-0.5 rounded-full">
                        —
                      </span>
                    ) : null}
                  </div>

                  <div className="p-3">
                    {!isEditing ? (
                      <>
                        {/* Aperçu du document existant */}
                        {docForType ? (
                          <>
                            <div
                              className="w-full h-32 bg-gray-100 border border-gray-100 rounded-lg mb-2"
                              style={{
                                backgroundImage: `url("${BASE_URL}webfree/fichier/${docForType?.id}")`,
                                backgroundRepeat: "no-repeat",
                                backgroundSize: "cover",
                                backgroundPosition: "center",
                              }}
                            />
                            <p className="text-xs text-gray-500 truncate mb-2">
                              {docForType.description}
                            </p>
                            <button
                              onClick={() => {
                                setEditingDocId(docForType.id);
                                setPostPaidDocuments([]);
                              }}
                              className="w-full flex items-center justify-center gap-2 py-2 rounded-lg border border-gray-200 hover:border-amber-400 hover:bg-amber-50 text-gray-600 hover:text-amber-700 text-xs font-semibold transition-all"
                            >
                              <Edit2 className="w-3.5 h-3.5" />
                              Remplacer
                            </button>
                          </>
                        ) : (
                          <label
                            onClick={() => {
                              setEditingDocId(docType.id);
                            }}
                            className="flex items-center justify-center gap-2 w-full px-3 py-6 rounded-lg border border-dashed border-gray-300 hover:border-amber-400 hover:bg-amber-50 cursor-pointer transition-all group"
                          >
                            <Upload className="w-4 h-4 text-gray-400 " />
                            <span className="text-xs text-gray-500 font-medium">
                              Ajouter un fichier
                            </span>
                          </label>
                        )}
                      </>
                    ) : (
                      /* Zone d'édition */
                      <div className="space-y-2">
                        {/* Fichier sélectionné */}
                        {uploadedForType && (
                          <div className="flex items-center gap-2 bg-amber-50 border border-amber-100 rounded-lg px-2 py-1.5">
                            <FileText className="w-3 h-3 text-amber-500 flex-shrink-0" />
                            <span className="flex-1 text-xs text-gray-700 truncate">
                              {uploadedForType.name}
                            </span>
                            <span className="text-xs text-gray-400 flex-shrink-0">
                              {formatFileSize(uploadedForType.size)}
                            </span>
                            <button
                              type="button"
                              onClick={() =>
                                handleRemoveDocument(uploadedForType.id)
                              }
                              className="w-5 h-5 rounded flex items-center justify-center bg-red-50 hover:bg-red-100 transition-colors flex-shrink-0"
                            >
                              <X className="w-3 h-3 text-red-500" />
                            </button>
                          </div>
                        )}

                        {/* Zone upload */}
                        <label className="flex items-center gap-2 w-full px-3 py-2.5 rounded-lg border border-dashed border-gray-300 hover:bg-amber-50 cursor-pointer transition-all group">
                          <Upload className="w-3.5 h-3.5 text-gray-400  flex-shrink-0" />
                          <span className="text-xs text-gray-500 ">
                            {uploadedForType
                              ? "Remplacer"
                              : "Choisir un fichier"}
                          </span>
                          <input
                            type="file"
                            className="hidden"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={(e) =>
                              handleAddDocument(e, docType.value)
                            }
                          />
                        </label>

                        {/* Actions */}
                        <div className="flex gap-2 pt-1">
                          <button
                            onClick={() => {
                              setEditingDocId(null);
                              setPostPaidDocuments((prev) =>
                                prev.filter((d) => d.type !== docType.value),
                              );
                            }}
                            className="flex-1 py-2 rounded-lg border border-gray-200 text-xs font-medium text-gray-500 hover:bg-gray-50 transition-colors"
                          >
                            Annuler
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <button
            onClick={() => handleModifyType()}
            disabled={isSubmitting || postPaidDocuments.length === 0}
            className="my-4 flex-1 py-2 px-3 rounded-lg bg-black text-white text-xs font-semibold disabled:bg-gray-300 disabled:cursor-not-allowed hover:bg-gray-800 transition-colors flex items-center justify-center gap-1.5"
          >
            {isSubmitting ? (
              <>
                <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Envoi...
              </>
            ) : (
              <>
                <Upload className="w-3 h-3" />
                Enregistrer les modifications
              </>
            )}
          </button>
        </div>
      </div>
      <div className="modal-backdrop" onClick={onClose} />
    </dialog>
  );
};

// ─────────────────────────────────────────────
// Modal Détails intention
// ─────────────────────────────────────────────
const DetailsModal = ({ item, onClose, setId, msg, setMsg }) => (
  <dialog
    open
    className="modal bg-black/60 backdrop-blur-sm"
    style={{ zIndex: 9999 }}
  >
    <div className="modal-box w-11/12 max-w-2xl bg-white rounded-2xl p-0 overflow-hidden shadow-2xl">
      <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-amber-400 flex items-center justify-center flex-shrink-0">
            <Eye className="w-4 h-4 text-white" />
          </div>
          <div>
            <h2 className="font-bold text-gray-900 text-base">
              Détails — N° {item?.numeroTicket}
            </h2>
            <p className="text-xs text-gray-400 my-0.5">
              {item?.client?.nomPrenoms}
            </p>
            <StatutBadge statut={item?.statusValidationPostpaid} />
          </div>
          {item?.isGele && item?.statusValidationPostpaid === "VALIDE" && (
            <button
              onClick={() => {
                setId(item.id);
                setMsg("");
                document.getElementById("delete").showModal();
              }}
              className="btn btn-sm bg-main text-xs"
            >
              Degeler le rendez-vous
            </button>
          )}
        </div>
        <button
          onClick={onClose}
          className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-red-50 hover:text-red-500 flex items-center justify-center transition-colors text-gray-500"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {item?.commentaireValidationPostpaid && (
        <div className="px-6 py-4 bg-red-50 border-l-4 border-red-400 space-y-1">
          <p className="text-sm font-medium text-gray-700">Motif de rejet</p>
          <p className="text-xs text-gray-500">
            {item?.commentaireValidationPostpaid}
          </p>
        </div>
      )}

      <div className="overflow-y-auto max-h-[75vh] px-6 py-5">
        <p className="text-red-500 text-sm">{msg}</p>
        <DetailIntention item={item} />
      </div>
    </div>
    <div className="modal-backdrop" onClick={onClose} />
  </dialog>
);

// ─────────────────────────────────────────────
// Carte abonnement
// ─────────────────────────────────────────────
const AbonnementCard = ({
  item,
  onDocumentsClick,
  onStatutClick,
  onDetailsClick,
  setSelectedItemForEdit,
}) => (
  <div className="relative bg-white rounded-2xl border shadow-sm hover:shadow-md hover:border-amber-200 transition-all duration-200 flex flex-col">
    {/* ── Header carte ── */}
    <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-dashed border-amber-300 rounded-t-2xl bg-amber-50/40">
      <p className="text-xs font-bold text-gray-700 tracking-wide">
        N° {item?.numeroTicket}
      </p>
      <StatutBadge statut={item?.statusValidationPostpaid} />
    </div>

    {/* ── Corps ── */}
    <div className="px-4 py-3 flex-1 space-y-3">
      <Periode item={item} />
      <Pbo pbo={item?.eligibilite?.pbo?.nom} port={item?.brin} />

      {/* Client */}
      <div className="flex gap-2 items-start">
        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
          <PersonIcon className="w-4 h-4 text-gray-500" />
        </div>
        <div className="min-w-0">
          <p className="text-[13px] font-semibold text-gray-800 truncate">
            {item?.client?.nomPrenoms}
          </p>
          <p className="text-xs text-gray-400 flex gap-1.5 flex-wrap">
            <span>{item?.client?.numero}</span>
            {item?.client?.secondNumero && (
              <>
                <span className="text-gray-200">|</span>
                <span>{item?.client?.secondNumero}</span>
              </>
            )}
          </p>
        </div>
      </div>

      {/* Agent */}
      {item?.agent && (
        <p className="text-xs text-gray-500">
          <span className="font-medium text-gray-600">Agent :</span>{" "}
          {item.agent.nomPrenoms}
        </p>
      )}

      {/* Forfait */}
      <p className="text-xs text-gray-600">
        <span className="font-medium">Forfait :</span>{" "}
        {item?.forfait?.designation}
        {item?.forfait?.categorie && (
          <span className="text-gray-400">
            {" "}
            — {item.forfait.categorie.replace(/_/g, " ").toLowerCase()}
          </span>
        )}
      </p>

      {/* Batiment / Equipement */}
      <div className="flex gap-3 py-2 border-t border-b border-gray-50">
        <p className="text-xs flex gap-1 items-center text-gray-500 font-medium">
          <HomeIcon className="text-gray-400" />
          {item?.batiment?.designation}
        </p>
        <p className="text-xs flex gap-1 items-center text-gray-500 font-medium">
          <ArchiveIcon className="text-gray-400" />
          {item?.equipement?.designation}
        </p>
      </div>

      {/* Localisation */}
      <p className="text-[13px] font-medium text-gray-700 flex items-center gap-1">
        <SewingPinFilledIcon className="text-amber-500 flex-shrink-0" />
        {[item?.commune, item?.quartier].filter(Boolean).join(", ")}
      </p>

      {/* Etape */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-400 font-medium">Étape</span>
        <span className="bg-blue-50 text-blue-700 border border-blue-100 py-0.5 px-2 rounded-lg font-medium text-xs truncate max-w-[180px]">
          {item?.status?.toLowerCase().replace(/_/g, " ")}
        </span>
      </div>

      {item.isGele && (
        <div className="flex items-center gap-2">
          <span className="bg-blue-50 text-orange-700 border border-blue-100 py-0.5 px-2 rounded-lg font-medium text-xs truncate max-w-[180px]">
            {item?.isGele && "Abonnement gelé"}
          </span>
        </div>
      )}

      {/* Docs count */}
      {item?.listeDocuements?.length > 0 && (
        <div className="flex items-center gap-1.5">
          <FileText className="w-3.5 h-3.5 text-amber-500" />
          <span className="text-xs text-gray-500">
            {item.listeDocuements.length} document
            {item.listeDocuements.length > 1 ? "s" : ""} joint
            {item.listeDocuements.length > 1 ? "s" : ""}
          </span>
        </div>
      )}
    </div>

    {/* ── Actions ── */}
    <div className="px-4 pb-4 pt-2 grid grid-cols-3 gap-2 border-t border-gray-50">
      {/* Détails */}
      <button
        onClick={() => onDetailsClick(item)}
        className="flex flex-col items-center gap-1 py-2.5 rounded-xl bg-gray-50 hover:bg-gray-100 border border-gray-100 hover:border-gray-200 transition-all group"
      >
        <Eye className="w-4 h-4 text-gray-500 group-hover:text-gray-700" />
        <span className="text-[10px] font-semibold text-gray-500 group-hover:text-gray-700">
          Détails
        </span>
      </button>

      {/* Dossiers */}
      <button
        onClick={() => onDocumentsClick(item)}
        className="flex flex-col items-center gap-1 py-2.5 rounded-xl bg-amber-50 hover:bg-amber-100 border border-amber-100 hover:border-amber-300 transition-all group disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <FileText className="w-4 h-4 text-amber-500 group-hover:text-amber-700" />
        <span className="text-[10px] font-semibold text-amber-600 group-hover:text-amber-800">
          Documents
        </span>
      </button>

      {checkTagValidity("MODIFIER") && (
        <button
          onClick={() => {
            setSelectedItemForEdit(item);
            document.getElementById("edit").showModal();
          }}
          className="flex flex-col items-center gap-1 py-2.5 rounded-xl bg-white hover:bg-gray-100 border border-gray-100 transition-all group text-gray-600 hover:text-gray-800 w-full"
        >
          <Edit2 className="w-4 h-4 text-gray-500 group-hover:text-gray-700" />
          <span className="text-[10px] font-semibold">Modifier</span>
        </button>
      )}
      {checkTagValidity("MODIFIER_STATUT_ABONNEMENT_POSTPAID") &&
        item?.statusValidationPostpaid === "EN_ATTENTE" && (
          <button
            onClick={() => onStatutClick(item)}
            className="flex flex-col items-center gap-1 py-2.5 rounded-xl bg-black hover:bg-gray-800 border border-black transition-all group"
          >
            <Edit2 className="w-4 h-4 text-white" />
            <span className="text-[10px] font-semibold text-white">Statut</span>
          </button>
        )}
    </div>
  </div>
);

// ─────────────────────────────────────────────
// Composant principal
// ─────────────────────────────────────────────
const ListeAbonnementsPostpaid = () => {
  const [id, setId] = useState(null);
  const [statut, setStatut] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [data, setData] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [selectedItemForDocuments, setSelectedItemForDocuments] =
    useState(null);
  const [selectedItemForStatut, setSelectedItemForStatut] = useState(null);
  const [selectedItemForDetails, setSelectedItemForDetails] = useState(null);
  const [selectedItemForEdit, setSelectedItemForEdit] = useState(null);

  const statutOptions = [
    {
      value: "",
      label: "Tout statut",
      icon: <Filter className="w-3.5 h-3.5" />,
    },
    {
      value: "EN_ATTENTE",
      label: "En attente",
      icon: <Clock3 className="w-3.5 h-3.5" />,
    },
    {
      value: "VALIDE",
      label: "Validés",
      icon: <CheckCircle2 className="w-3.5 h-3.5" />,
    },
    {
      value: "REJETE",
      label: "Rejetés",
      icon: <XCircle className="w-3.5 h-3.5" />,
    },
  ];

  const fetchData = (
    currentPage = 0,
    currentStatut = statut,
    param = search,
  ) => {
    setLoading(true);
    find(
      `${LISTE_POSTPAID}?statut=${currentStatut}&page=${currentPage}&size=9&param=${param}`,
    )
      .then((res) => {
        setData(res?.data?.donnee?.data ?? res?.data?.donnee ?? []);
        setTotalPages(res?.data?.donnee?.totalPages ?? 1);
      })
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData(0, statut, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statut]);

  const degelerRdv = async (id) => {
    await store(`${DEGELER_POSTPAID}/${id}`, "application/json", {})
      .then((res) => {
        if (res.status === 200) {
          fetchData(0, statut, "");
          document.getElementById("delete").close();
          setSelectedItemForDetails(null);
          toast.success("Rendez-vous degelé avec succès");
        }
      })
      .catch((e) => {
        setMsg(
          e?.response?.data?.donnee || "Erreur lors du degel du rendez-vous",
        );
        document.getElementById("delete").close();
      });
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(0);
    fetchData(0, statut, e.target.value);
  };

  const handleStatut = (val) => {
    setStatut(val);
    setPage(0);
    setSearch("");
  };

  const handlePage = (_, newPage) => {
    setPage(newPage - 1);
    fetchData(newPage - 1, statut, search);
  };

  return (
    <div className="w-full my-3">
      {/* ── Filtres + Recherche ── */}
      <div className="mb-5 flex flex-wrap items-center gap-2">
        {/* Recherche */}
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={handleSearch}
            placeholder="Rechercher un abonnement..."
            className="w-full pl-9 pr-4 h-10 text-sm input input-bordered rounded-xl"
          />
        </div>

        <dialog id="edit" className="modal ">
          <div className="modal-box w-10/12 max-w-2xl">
            <div className="modal-action">
              <h1 className="mr-auto text-2xl font-bold font-mtn">
                Modification de l'abonnement
              </h1>
              <form method="dialog">
                <button
                  id="fermer"
                  className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-100 hover:bg-red-200 text-red-600 hover:text-red-600 font-semibold"
                >
                  ✕
                </button>
              </form>
            </div>
            <p className="text-sm text-red-500">{msg}</p>
            <UpdateDemandeIntention
              setMessage={setMsg}
              item={selectedItemForEdit ?? ""}
            />
          </div>
        </dialog>
        {/* Delete Modal */}
        <dialog id="delete" className="modal">
          <div className="modal-box rounded-2xl p-6 max-w-md">
            <p className="text-base font-bold text-gray-900 mb-1">
              Confirmer le degele du rendez-vous
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Êtes-vous sûr de vouloir degeler ce rendez-vous ? Cette action est
              irréversible.
            </p>
            <div className="flex justify-end gap-2">
              <form method="dialog">
                <button className="h-9 px-4 rounded-lg text-sm font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 transition">
                  Annuler
                </button>
              </form>
              <form method="dialog">
                <button
                  onClick={() => {
                    degelerRdv(id);
                  }}
                  className="h-9 px-4 rounded-lg text-sm font-medium bg-main hover:bg-main text-black transition"
                >
                  Degeler
                </button>
              </form>
            </div>
          </div>
        </dialog>

        {/* Tabs statut */}
        <div className="flex items-center bg-gray-100 rounded-xl p-1 gap-1">
          {statutOptions.map((opt) => (
            <button
              key={opt.value}
              onClick={() => handleStatut(opt.value)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-150 ${
                statut === opt.value
                  ? opt.value === "VALIDE"
                    ? "bg-white shadow-sm text-emerald-700 border border-emerald-100"
                    : opt.value === "REJETE"
                      ? "bg-white shadow-sm text-red-600 border border-red-100"
                      : "bg-white shadow-sm text-amber-700 border border-amber-100"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {opt.icon}
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Grille ── */}
      {loading ? (
        <div className="grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="bg-white rounded-2xl border border-slate-100 h-64 animate-pulse"
            >
              <div className="h-12 bg-amber-50 rounded-t-2xl border-b border-dashed border-amber-100" />
              <div className="p-4 space-y-3">
                <div className="h-3 bg-gray-100 rounded-full w-3/4" />
                <div className="h-3 bg-gray-100 rounded-full w-1/2" />
                <div className="h-3 bg-gray-100 rounded-full w-2/3" />
                <div className="h-3 bg-gray-100 rounded-full w-1/3" />
              </div>
            </div>
          ))}
        </div>
      ) : data.length === 0 ? (
        <div className="py-20 flex flex-col items-center justify-center text-gray-400">
          <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
            <FileText className="w-8 h-8 text-gray-300" />
          </div>
          <p className="text-sm font-medium">
            Aucun abonnement postpaid{" "}
            {statut === "VALIDE"
              ? "validé"
              : statut === "REJETE"
                ? "rejeté"
                : "en attente"}
            .
          </p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-4">
          {data.map((item) => (
            <AbonnementCard
              key={item?.id ?? item?.numeroTicket}
              item={item}
              onDocumentsClick={setSelectedItemForDocuments}
              onStatutClick={setSelectedItemForStatut}
              onDetailsClick={setSelectedItemForDetails}
              setSelectedItemForEdit={setSelectedItemForEdit}
            />
          ))}
        </div>
      )}

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <div className="my-4 flex justify-end">
          <Pagination
            page={page + 1}
            count={totalPages}
            onChange={handlePage}
            variant="outlined"
            color="primary"
            shape="rounded"
          />
        </div>
      )}

      {/* ── Modals ── */}
      {selectedItemForDetails && (
        <DetailsModal
          setId={setId}
          setMsg={setMsg}
          msg={msg}
          item={selectedItemForDetails}
          onClose={() => setSelectedItemForDetails(null)}
        />
      )}

      {selectedItemForDocuments && (
        <DocumentsModal
          item={selectedItemForDocuments}
          onClose={() => setSelectedItemForDocuments(null)}
          onDocumentUpdated={() => fetchData(page, statut, search)}
        />
      )}

      {selectedItemForStatut && (
        <StatutModal
          item={selectedItemForStatut}
          onClose={() => setSelectedItemForStatut(null)}
          onStatutUpdated={() => fetchData(page, statut, search)}
        />
      )}
    </div>
  );
};

export default ListeAbonnementsPostpaid;
